import { Component, Fragment } from "react"
import PropTypes from "prop-types"

import Step from "./step"
import Trigger from "./trigger"

import styles from "./dynamic_applet.module.scss"

class Endpoints extends Component {
  render() {
    return (
      <section className={styles.dynamic_applet}>
        <h2>Connect API endpoints</h2>
        <p>
          Below you&lsquo;ll find the unique API endpoints to access{" "}
          {this.props.channels.map(c => c.name).join(" and ")} actions and
          triggers on behalf of a user to power this connection experience. You
          can also test with your own{" "}
          {this.props.channels.map(c => c.name).join(" or ")} account. View{" "}
          <a
            className="link-inline"
            href={this.props.docs_url}
            target="_blank"
            rel="noreferrer"
          >
            Connect API documentation
          </a>{" "}
          for more detail.
        </p>

        {this.props.channels.map(channel => {
          const appletTriggersForChannel = this.props.triggers.filter(
            t => t.channel_module_name == channel.module_name
          )

          const appletQueriesForChannel = this.props.queries.filter(
            q => q.channel_module_name == channel.module_name
          )

          const appletActionsForChannel = this.props.actions.filter(
            a => a.channel_module_name == channel.module_name
          )

          return (
            <Fragment key={channel.name}>
              <span className={styles["main-section-header"]}>
                <h3>
                  {channel.lrg_variant_image_url && (
                    <span
                      className={styles.icon}
                      style={{
                        backgroundColor: "#" + channel.brand_color,
                      }}
                    >
                      <img
                        src={channel.lrg_variant_image_url}
                        title={channel.name}
                        alt={channel.name}
                      />
                    </span>
                  )}
                  {channel.name} Triggers
                </h3>
              </span>
              {appletTriggersForChannel.length === 0 &&
                (channel.triggers.length > 0 ? (
                  <p className={styles["no-steps"]}>
                    No {channel.name} triggers added yet.{" "}
                    <a className="link-inline" href={this.props.edit_url}>
                      Edit this connection
                    </a>
                    to add more triggers.
                  </p>
                ) : (
                  <p className={styles["no-steps"]}>
                    There are no triggers available for the {channel.name}{" "}
                    service
                  </p>
                ))}
              {appletTriggersForChannel.map((appletTrigger, index) => (
                <Trigger
                  key={index}
                  connection_id={this.props.id}
                  name={appletTrigger.trigger.name}
                  description={appletTrigger.trigger.description}
                  other_channel_brand_color={channel.brand_color}
                  full_module_name={appletTrigger.trigger.full_module_name}
                  ingredients={appletTrigger.trigger.ingredients}
                  channel_user_id={this.props.channel_user_id}
                  channel_api_host={this.props.channel_api_host}
                  docs_url={this.props.docs_url}
                  anchor={appletTrigger.trigger.name
                    .split(" ")
                    .join("-")
                    .toLowerCase()}
                />
              ))}
              <span className={styles["main-section-header"]}>
                <h3>
                  {channel.lrg_variant_image_url && (
                    <span
                      className={styles.icon}
                      style={{
                        backgroundColor: "#" + channel.brand_color,
                      }}
                    >
                      <img
                        src={channel.lrg_variant_image_url}
                        title={channel.name}
                        alt={channel.name}
                      />
                    </span>
                  )}
                  {channel.name} Queries
                </h3>
              </span>
              {appletQueriesForChannel.length === 0 &&
                (channel.queries.length > 0 ? (
                  <p className={styles["no-steps"]}>
                    No {channel.name} queries added yet.{" "}
                    <a className="link-inline" href={this.props.edit_url}>
                      Edit this connection
                    </a>
                    to add more queries.
                  </p>
                ) : (
                  <p className={styles["no-steps"]}>
                    There are no queries available for the {channel.name}{" "}
                    service
                  </p>
                ))}
              {appletQueriesForChannel.map((appletQuery, index) => (
                <Step
                  key={index}
                  step_type="query"
                  step_type_sentence="Perform the query"
                  step_path={`/queries/${appletQuery.query.full_module_name}/perform`}
                  step_field_options_path={`/queries/${appletQuery.query.full_module_name}/field_options`}
                  enabled_for_user={this.props.enabled_for_user}
                  connection_id={this.props.id}
                  name={appletQuery.query.name}
                  description={appletQuery.query.description}
                  channel_name={channel.name}
                  channel_brand_color={channel.brand_color}
                  channel_image_url={channel.image_url}
                  full_module_name={appletQuery.query.full_module_name}
                  fields={appletQuery.fields}
                  channel_user_id={this.props.channel_user_id}
                  live_values={this.props.query_values}
                  connect_url={this.props.connect_url}
                  docs_url={this.props.docs_url}
                  anchor={appletQuery.query.name
                    .split(" ")
                    .join("-")
                    .toLowerCase()}
                />
              ))}
              <span className={styles["main-section-header"]}>
                <h3>
                  {channel.lrg_variant_image_url && (
                    <span
                      className={styles.icon}
                      style={{
                        backgroundColor: "#" + channel.brand_color,
                      }}
                    >
                      <img
                        src={channel.lrg_variant_image_url}
                        title={channel.name}
                        alt={channel.name}
                      />
                    </span>
                  )}
                  {channel.name} Actions
                </h3>
              </span>
              {appletActionsForChannel.length == 0 &&
                (channel.actions.length > 0 ? (
                  <p className={styles["no-steps"]}>
                    No {channel.name} actions added yet.{" "}
                    <a className="link-inline" href={this.props.edit_url}>
                      Edit this connection
                    </a>
                    to add more actions.
                  </p>
                ) : (
                  <p className={styles["no-steps"]}>
                    There are no actions available for the {channel.name}{" "}
                    service
                  </p>
                ))}
              {appletActionsForChannel.map((appletAction, index) => (
                <Step
                  key={index}
                  step_type="action"
                  step_type_sentence="Run the action"
                  step_path={`/actions/${appletAction.action.full_module_name}/run`}
                  step_field_options_path={`/actions/${appletAction.action.full_module_name}/field_options`}
                  enabled_for_user={this.props.enabled_for_user}
                  connection_id={this.props.id}
                  name={appletAction.action.name}
                  description={appletAction.action.description}
                  channel_name={channel.name}
                  channel_brand_color={channel.brand_color}
                  channel_image_url={channel.image_url}
                  full_module_name={appletAction.action.full_module_name}
                  fields={appletAction.fields}
                  channel_user_id={this.props.channel_user_id}
                  live_values={this.props.action_values}
                  connect_url={this.props.connect_url}
                  docs_url={this.props.docs_url}
                  mktApiUrl={this.props.mktApiUrl}
                  anchor={appletAction.action.name
                    .split(" ")
                    .join("-")
                    .toLowerCase()}
                />
              ))}
            </Fragment>
          )
        })}
      </section>
    )
  }
}

Endpoints.propTypes = {
  id: PropTypes.string.isRequired,
  enabled_for_user: PropTypes.bool.isRequired,
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      brand_color: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image_url: PropTypes.string.isRequired,
      lrg_variant_image_url: PropTypes.string.isRequired,
    })
  ),
  docs_url: PropTypes.string.isRequired,
  mktApiUrl: PropTypes.string.isRequired,
  connect_url: PropTypes.string.isRequired,
  edit_url: PropTypes.string.isRequired,
  actions: PropTypes.array.isRequired,
  triggers: PropTypes.array.isRequired,
  channel_user_id: PropTypes.string.isRequired,
  channel_api_host: PropTypes.string.isRequired,
  action_values: PropTypes.object,
  query_values: PropTypes.object,
}

export default Endpoints
